import {
  collection,
  documentId,
  getDocs,
  onSnapshot,
  query,
  where,
  type Query,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  type QuerySnapshot,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  buildGeoQueries,
  fetchGeoPage,
  getGeoBounds,
  mergeGeoQueryDocs,
  type GeoBound,
  type GeoCursor,
} from '@/lib/geoQuery';
import { calculateAge, formatLastActive, UserProfile } from '@/types';
import { calculateDistance } from '@/utils/geo';

export interface DiscoveryFilter {
  ageRange?: [number, number];
  intentions?: string[];
  distance?: number;
  currentLatitude?: number;
  currentLongitude?: number;
  openTo?: string[];
  relationshipStatus?: string[];
  genderMode?: 'all' | 'mine';
  ethnicityMode?: 'all' | 'mine';
  gender?: string[];
  ethnicity?: string[];
  blockedUids?: string[];
  blockedByUids?: string[];
  availableToday?: boolean;
  favoritesOnly?: boolean;
}

export interface DiscoveryFeedPage {
  profiles: UserProfile[];
  /** Per-bound resume state, index-aligned with the geohash bounds. */
  cursors: GeoCursor[];
  /** True while at least one bound may still have unfetched docs. */
  hasMore: boolean;
}

/** Per-bound document cap for one fetch round. */
export const QUERY_LIMIT = 150;

/** Grid cards stop showing the last-active badge once it reaches 2 weeks. */
const LAST_ACTIVE_BADGE_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

function discoveryConstraints(filters: DiscoveryFilter): QueryConstraint[] {
  const constraints: QueryConstraint[] = [where('visible', '==', true)];

  if (filters.intentions && filters.intentions.length > 0) {
    constraints.push(where('intentions', 'array-contains-any', filters.intentions));
  }

  return constraints;
}

function discoveryBounds(filters: DiscoveryFilter): GeoBound[] {
  const { currentLatitude, currentLongitude, distance } = filters;
  const center =
    typeof currentLatitude === 'number' &&
    typeof currentLongitude === 'number' &&
    typeof distance === 'number'
      ? { latitude: currentLatitude, longitude: currentLongitude }
      : undefined;
  return getGeoBounds(center, distance);
}

/**
 * The filters actually engaged for a call — the `filters` object always
 * carries defaults (ageRange, distance, block lists), so this surfaces what
 * is really constraining results in the log line.
 */
function describeActiveFilters(filters: DiscoveryFilter): Record<string, unknown> {
  const active: Record<string, unknown> = {};
  if (filters.ageRange) active.ageRange = filters.ageRange;
  if (filters.intentions?.length) active.intentions = filters.intentions;
  if (typeof filters.distance === 'number') {
    active.distance = `${filters.distance}km @ ${filters.currentLatitude},${filters.currentLongitude}`;
  }
  if (filters.openTo?.length) active.openTo = filters.openTo;
  if (filters.relationshipStatus?.length) {
    active.relationshipStatus = filters.relationshipStatus;
  }
  if (filters.genderMode === 'mine') active.gender = filters.gender;
  if (filters.ethnicityMode === 'mine') active.ethnicity = filters.ethnicity;
  if (filters.blockedUids?.length) active.blockedUids = filters.blockedUids.length;
  if (filters.blockedByUids?.length) active.blockedByUids = filters.blockedByUids.length;
  if (filters.availableToday) active.availableToday = true;
  return active;
}

type FilterRejection = { reason: string; detail: string };

function checkBlocked(data: UserProfile, filters: DiscoveryFilter): FilterRejection | null {
  if (filters.blockedUids?.includes(data.uid)) {
    return { reason: 'blocked', detail: 'in blockedUids' };
  }
  if (filters.blockedByUids?.includes(data.uid)) {
    return { reason: 'blocked', detail: 'in blockedByUids' };
  }
  return null;
}

function checkAgeRange(data: UserProfile, filters: DiscoveryFilter): FilterRejection | null {
  if (!filters.ageRange) return null;
  const [minAge, maxAge] = filters.ageRange;
  const age = calculateAge(data.birthday);
  if (age < minAge || age > maxAge) {
    return {
      reason: 'age',
      detail: `age=${age} (birthday=${data.birthday}) outside ${minAge}-${maxAge}`,
    };
  }
  return null;
}

function checkOpenTo(data: UserProfile, filters: DiscoveryFilter): FilterRejection | null {
  const { openTo } = filters;
  if (!openTo || openTo.length === 0) return null;
  const candidateOpenTo = data.openTo ?? [];
  if (candidateOpenTo.length === 0) return null;
  const overlap = openTo.some(id =>
    candidateOpenTo.some(v => v.toLowerCase().includes(id.toLowerCase()))
  );
  if (!overlap) {
    return {
      reason: 'openTo',
      detail: `theirs=${JSON.stringify(candidateOpenTo)} required=${JSON.stringify(openTo)}`,
    };
  }
  return null;
}

function checkRelationshipStatus(
  data: UserProfile,
  filters: DiscoveryFilter
): FilterRejection | null {
  const { relationshipStatus } = filters;
  if (!relationshipStatus || relationshipStatus.length === 0) return null;
  const status = data.relationshipStatus;
  if (!status) return null;
  const normalized = status.toLowerCase().replace(/\s+/g, '');
  if (!relationshipStatus.includes(normalized)) {
    return {
      reason: 'relationshipStatus',
      detail: `theirs=${normalized} allowed=${JSON.stringify(relationshipStatus)}`,
    };
  }
  return null;
}

function checkGender(data: UserProfile, filters: DiscoveryFilter): FilterRejection | null {
  const { genderMode, gender } = filters;
  if (genderMode !== 'mine' || !gender || gender.length === 0) return null;
  if (!data.gender?.some(g => gender.includes(g))) {
    return {
      reason: 'gender',
      detail: `theirs=${JSON.stringify(data.gender)} mine=${JSON.stringify(gender)}`,
    };
  }
  return null;
}

function checkEthnicity(data: UserProfile, filters: DiscoveryFilter): FilterRejection | null {
  const { ethnicityMode, ethnicity } = filters;
  if (ethnicityMode !== 'mine' || !ethnicity || ethnicity.length === 0) return null;
  let theirs: string[] = [];
  if (Array.isArray(data.ethnicity)) {
    theirs = data.ethnicity;
  } else if (data.ethnicity) {
    theirs = [data.ethnicity];
  }
  if (!theirs.some(e => ethnicity.includes(e))) {
    return {
      reason: 'ethnicity',
      detail: `theirs=${JSON.stringify(theirs)} mine=${JSON.stringify(ethnicity)}`,
    };
  }
  return null;
}

function checkAvailableToday(data: UserProfile, filters: DiscoveryFilter): FilterRejection | null {
  if (filters.availableToday && !data.availableToday) {
    return { reason: 'availableToday', detail: 'not live today' };
  }
  return null;
}

function checkDistance(data: UserProfile, filters: DiscoveryFilter): FilterRejection | null {
  if (typeof filters.distance !== 'number') return null;
  const d = calculateDistance(
    filters.currentLatitude,
    filters.currentLongitude,
    data.latitude,
    data.longitude
  );
  if (d != null && d > filters.distance) {
    return {
      reason: 'distance',
      detail: `${d.toFixed(1)}km > ${filters.distance}km (theirs=${data.latitude},${data.longitude})`,
    };
  }
  return null;
}

/**
 * Returns which filter rejected the profile (with the compared values for
 * logging), or null when it passes. Same check order as before, but every
 * rejection names its rule instead of collapsing into a generic 'filters'.
 */
function discoveryFilterRejection(
  data: UserProfile,
  filters: DiscoveryFilter
): FilterRejection | null {
  return (
    checkBlocked(data, filters) ??
    checkAgeRange(data, filters) ??
    checkOpenTo(data, filters) ??
    checkRelationshipStatus(data, filters) ??
    checkGender(data, filters) ??
    checkEthnicity(data, filters) ??
    checkAvailableToday(data, filters) ??
    checkDistance(data, filters)
  );
}

/** Derives the display fields (`age`, `lastActive`, `onboarded`) the grid needs. */
function toDisplayProfile(data: UserProfile): UserProfile {
  const lastActiveAt = data.lastActiveAt ?? data.lastLoggedInAt;
  const inactiveMs = lastActiveAt ? Date.now() - new Date(lastActiveAt).getTime() : 0;
  return {
    ...data,
    onboarded: data.onboarded ?? false,
    age: calculateAge(data.birthday),
    lastActive: inactiveMs < LAST_ACTIVE_BADGE_WINDOW_MS ? formatLastActive(lastActiveAt) : '',
  };
}

/**
 * Converts raw docs into displayable profiles: drops the current user and
 * duplicate uids, applies the client-side filters, and derives display
 * fields. Docs without a uid can't be deduplicated, so they're dropped too.
 */
export function filterDiscoveryProfiles(
  docs: QueryDocumentSnapshot[],
  currentUserId: string,
  filters: DiscoveryFilter
): UserProfile[] {
  const profiles: UserProfile[] = [];
  const seenUids = new Set<string>();
  const skipReasons: Record<string, number> = {};

  const logSkip = (reason: string, detail: string) => {
    console.log(`[filterDiscoveryProfiles] skip (${reason}): ${detail}`);
    skipReasons[reason] = (skipReasons[reason] ?? 0) + 1;
  };

  console.log(
    `[filterDiscoveryProfiles] fetched ${docs.length} docs; active filters: ${JSON.stringify(
      describeActiveFilters(filters)
    )}`
  );

  for (const docSnap of docs) {
    const data = docSnap.data() as UserProfile;

    if (!data.uid) {
      logSkip('no uid', `docId=${docSnap.id} name=${data.name}`);
      continue;
    }

    if (data.uid === currentUserId) {
      logSkip('current user', `uid=${data.uid}`);
      continue;
    }

    if (seenUids.has(data.uid)) {
      logSkip('duplicate', `uid=${data.uid} docId=${docSnap.id}`);
      continue;
    }
    seenUids.add(data.uid);

    const rejection = discoveryFilterRejection(data, filters);
    if (rejection) {
      logSkip(
        `filters:${rejection.reason}`,
        `uid=${data.uid} name=${data.name} — ${rejection.detail}`
      );
      continue;
    }

    profiles.push(toDisplayProfile(data));
  }

  console.log(
    `[filterDiscoveryProfiles] summary: accepted=${profiles.length}, skipReasons=${JSON.stringify(
      skipReasons
    )}`
  );

  return profiles;
}

/**
 * Removes duplicate profiles by uid, keeping the first occurrence. Pages can
 * overlap when the realtime subscription rewrites the first page while stale
 * cursors remain in later page params, or when a refetch shifts page
 * boundaries between requests.
 */
export function dedupeProfiles(profiles: UserProfile[]): UserProfile[] {
  const seen = new Set<string>();
  return profiles.filter(profile => {
    if (!profile.uid || seen.has(profile.uid)) return false;
    seen.add(profile.uid);
    return true;
  });
}

/** Favorites view: keeps `visible`, non-viewer, unblocked profiles; preference filters don't apply. */
export function filterFavoriteProfiles(
  docs: QueryDocumentSnapshot[],
  currentUserId: string,
  filters: DiscoveryFilter
): UserProfile[] {
  const { blockedUids, blockedByUids } = filters;
  const profiles: UserProfile[] = [];

  for (const docSnap of docs) {
    const data = docSnap.data() as UserProfile;
    if (!data.uid || data.uid === currentUserId) continue;
    if (data.visible !== true) continue;
    if (blockedUids?.includes(data.uid) || blockedByUids?.includes(data.uid)) continue;
    profiles.push(toDisplayProfile(data));
  }

  return profiles;
}

/** Firestore `in` queries accept at most 30 values. */
const DOCUMENT_ID_IN_CHUNK = 30;

export const discoveryService = {
  /**
   * Fetches one round of the discovery feed: up to `QUERY_LIMIT` docs per
   * geohash bound. Pass the returned `cursors` back in for the next round;
   * each bound paginates independently until it reports `'done'`, so callers
   * that loop until `hasMore` is false see every matching user — no bound is
   * abandoned once a global cursor moves past it.
   *
   * Other filters (age, gender, ethnicity, openTo, relationshipStatus, blocks,
   * current user) are applied client-side in `filterDiscoveryProfiles`.
   */
  async getDiscoveryFeed(
    currentUserId: string,
    filters: DiscoveryFilter = {},
    cursors: GeoCursor[] | null = null
  ): Promise<DiscoveryFeedPage> {
    const bounds = discoveryBounds(filters);
    const page = await fetchGeoPage(collection(db, 'users'), {
      bounds,
      constraints: discoveryConstraints(filters),
      cursors,
      limit: QUERY_LIMIT,
    });
    return {
      profiles: filterDiscoveryProfiles(page.docs, currentUserId, filters),
      cursors: page.cursors,
      hasMore: page.hasMore,
    };
  },

  /**
   * Live-updates the head of the feed: one subscription per bound, each
   * capped at `QUERY_LIMIT` docs (the same range the first fetch round
   * covers). Delivers freshly filtered profiles for the caller to splice
   * over its first cached page.
   */
  subscribeToDiscoveryFeed(
    currentUserId: string,
    filters: DiscoveryFilter = {},
    callback: (profiles: UserProfile[]) => void
  ): () => void {
    const queries = buildGeoQueries(collection(db, 'users'), {
      bounds: discoveryBounds(filters),
      constraints: discoveryConstraints(filters),
      limit: QUERY_LIMIT,
    }).filter((q): q is Query => q !== null);
    const snaps: (QuerySnapshot | undefined)[] = Array.from<QuerySnapshot | undefined>({
      length: queries.length,
    });
    let lastSignature: string | undefined;

    const unsubs = queries.map((q, i) =>
      onSnapshot(
        q,
        snap => {
          // Skip cache emits — the initial getDocs round already covers the
          // head; delivering stale cached data would flash outdated profiles.
          if (snap.metadata.fromCache) return;
          snaps[i] = snap;
          if (!snaps.every(Boolean)) return;
          const allDocs = mergeGeoQueryDocs(snaps as QuerySnapshot[]);
          const profiles = filterDiscoveryProfiles(allDocs, currentUserId, filters);
          // Any matching doc write (e.g. activity heartbeats) re-emits the
          // whole query; don't push an identical list downstream.
          const signature = JSON.stringify(profiles);
          if (signature === lastSignature) return;
          lastSignature = signature;
          callback(profiles);
        },
        error => {
          console.error('Discovery feed subscription failed:', error);
        }
      )
    );

    return () => unsubs.forEach(u => u());
  },

  /**
   * Fetches starred `users` docs by id; preference filters don't apply,
   * blocks and `visible` do. Single page (`hasMore: false`).
   */
  async getFavoritesFeed(
    currentUserId: string,
    favoriteUids: string[],
    filters: DiscoveryFilter = {}
  ): Promise<DiscoveryFeedPage> {
    if (favoriteUids.length === 0) {
      return { profiles: [], cursors: [], hasMore: false };
    }

    const snaps = await Promise.all(
      Array.from({ length: Math.ceil(favoriteUids.length / DOCUMENT_ID_IN_CHUNK) }, (_, i) =>
        getDocs(
          query(
            collection(db, 'users'),
            where(
              documentId(),
              'in',
              favoriteUids.slice(i * DOCUMENT_ID_IN_CHUNK, (i + 1) * DOCUMENT_ID_IN_CHUNK)
            )
          )
        )
      )
    );

    return {
      profiles: filterFavoriteProfiles(
        snaps.flatMap(s => s.docs),
        currentUserId,
        filters
      ),
      cursors: [],
      hasMore: false,
    };
  },
};
